import { useMemo } from 'react';
import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import { REVIEW_STATUS, REVIEW_STATUS_LABELS } from '@/constants/reviewStatus';
import { useAmZmFilterOptions } from '@/hooks/useAmZmFilterOptions';
import { useDistrictThanaFilterOptions } from '@/hooks/useDistrictThanaFilterOptions';
import { DISTRICT_FIRST_FIELD_STATE, getDateFromToFilterFields } from '@/utils/filterFields';
import {
  FILTER_KEYS,
  getSelectFieldsWhenOptionsExist,
  parseNumberFilter,
  toFilterOptions,
} from '@/utils/filters';
import {
  TEACHER_INACTIVATION_REASON,
  TEACHER_INACTIVATION_REASON_LABELS,
} from '../constants/teacherInactivation.constants';
import { REASON_FILTER_KEY, REVIEW_STATUS_FILTER_KEY } from '../utils/teacherInactivationFilters';

const STATUS_OPTIONS: FilterOption[] = REVIEW_STATUS.map((status) => ({
  label: REVIEW_STATUS_LABELS[status],
  value: status,
}));

const REASON_OPTIONS: FilterOption[] = TEACHER_INACTIVATION_REASON.map((reasonCode) => ({
  label: TEACHER_INACTIVATION_REASON_LABELS[reasonCode],
  value: reasonCode,
}));

interface UseTeacherInactivationFilterFieldsParams {
  isEnabled: boolean;
  filterValues: Record<string, string>;
}

export function useTeacherInactivationFilterFields({
  isEnabled,
  filterValues,
}: UseTeacherInactivationFilterFieldsParams) {
  const amUserId = parseNumberFilter(filterValues[FILTER_KEYS.amUserId]);
  const districtId = parseNumberFilter(filterValues[FILTER_KEYS.districtId]);
  const {
    amOptions,
    zmOptions,
    hasOptionsError: hasHierarchyOptionsError,
  } = useAmZmFilterOptions({ isEnabled, amUserId });
  const {
    districts,
    thanas,
    hasOptionsError: hasGeoOptionsError,
  } = useDistrictThanaFilterOptions({ isEnabled, districtId });

  const fields = useMemo(
    (): FilterField[] => [
      ...getDateFromToFilterFields(),
      { key: REVIEW_STATUS_FILTER_KEY, label: 'Status', type: 'select', options: STATUS_OPTIONS },
      { key: REASON_FILTER_KEY, label: 'Reason', type: 'select', options: REASON_OPTIONS },
      {
        key: FILTER_KEYS.districtId,
        label: 'District',
        type: 'select',
        options: toFilterOptions(districts),
        searchable: true,
      },
      {
        key: FILTER_KEYS.thanaId,
        label: 'Thana',
        type: 'select',
        options: toFilterOptions(thanas),
        searchable: true,
        ...(districtId === undefined ? DISTRICT_FIRST_FIELD_STATE : {}),
      },
      ...getSelectFieldsWhenOptionsExist(FILTER_KEYS.amUserId, 'AM Name', amOptions),
      ...getSelectFieldsWhenOptionsExist(FILTER_KEYS.zmUserId, 'ZM Name', zmOptions),
    ],
    [districts, thanas, districtId, amOptions, zmOptions],
  );

  return { fields, hasOptionsError: hasHierarchyOptionsError || hasGeoOptionsError };
}
